import { useMemo, useState } from 'react';
import { useTrip } from '../lib/tripContext.jsx';
import { useUser } from '../lib/userContext.jsx';
import { useTestMode } from '../lib/testModeContext.jsx';
import { useTripJson, useWeather } from '../hooks/data.js';
import { useCoords } from '../hooks/useCoords.js';
import { rankScenarios } from '../lib/photoCoachingScoring.js';
import { isGoldenHour, nextGoldenHour } from '../lib/sun.js';

// Photo Coaching tab — field reference for technical settings + Wilman
// audio. The settings table is the load-bearing UI element: scannable
// in 8-10 seconds on a moving boat with cold fingers.
//
// Header strip: a single line of current context — light condition,
// time, golden-hour countdown — derived from GPS + weather + clock.
// Filter chips immediately below: Player + Camera body + Activity +
// Lens. A sort dropdown sits next to the chips. The full 23-card
// browse list renders by default; filters narrow it.
//
// Works fully offline:
//   • photo-coaching.json precaches via SW
//   • Audio MP3s precache via SW
//   • GPS falls back to last-known coords; weather to last-cached payload

const ACTIVITY_TYPE_LABELS = {
  whale_watching: 'Whale watch',
  glacier: 'Glacier',
  canoe: 'Canoe',
  golden_hour: 'Golden hour',
  eagle: 'Eagle',
  low_light_dinner: 'Low light',
  environmental: 'Environmental',
  fjord: 'Fjord',
  departure: 'Departure',
  wildlife: 'Wildlife',
  landscape: 'Landscape',
};

// WMO weather code → terse label for the condition strip. Mirrors the
// table on Today.jsx but kept inline here so the coaching tab doesn't
// drift if the strip's wording changes.
function weatherCodeLabel(code) {
  if (code == null) return null;
  if (code === 0) return 'CLEAR';
  if (code === 1) return 'MOSTLY CLEAR';
  if (code === 2) return 'PARTLY CLOUDY';
  if (code === 3) return 'OVERCAST';
  if (code === 45 || code === 48) return 'FOG';
  if (code >= 51 && code <= 57) return 'DRIZZLE';
  if (code >= 61 && code <= 67) return 'RAIN';
  if (code >= 71 && code <= 77) return 'SNOW';
  if (code >= 80 && code <= 82) return 'SHOWERS';
  if (code >= 85 && code <= 86) return 'SNOW SHOWERS';
  if (code >= 95) return 'THUNDER';
  return null;
}

function formatTimeLocal(date, tz) {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

function formatGoldenCountdown(ms) {
  if (ms <= 0) return null;
  const totalMin = Math.round(ms / 60000);
  const hr = Math.floor(totalMin / 60);
  const min = totalMin % 60;
  if (hr === 0) return `${min}MIN`;
  return `${hr}HR ${String(min).padStart(2, '0')}MIN`;
}

export default function PhotoCoaching() {
  const { tripId, trip } = useTrip();
  const { activePlayerId } = useUser();
  const testMode = useTestMode();
  const data = useTripJson(tripId, 'photo-coaching.json');
  const excursionsData = useTripJson(tripId, 'excursions.json');
  const { coords, status: coordsStatus } = useCoords();

  // GPS fallback chain: live coords → trip destination → Juneau.
  const lat = coords?.lat ?? trip?.destination?.lat ?? 58.3019;
  const lng = coords?.lng ?? trip?.destination?.lng ?? -134.4197;
  const tz = trip?.dates?.timezone || 'America/Juneau';
  const now = useMemo(
    () => testMode?.now?.() || new Date(),
    [testMode]
  );

  const weather = useWeather({ lat, lng, hours: 24, timezone: tz });

  // Current weather code from the hourly array — pick the bucket whose
  // ISO timestamp matches the current local hour.
  const currentWeatherLabel = useMemo(() => {
    const h = weather.data?.hourly;
    if (!h?.time?.length) return null;
    const target = now.toISOString().slice(0, 13); // YYYY-MM-DDTHH
    let idx = h.time.findIndex((t) => t.startsWith(target));
    if (idx === -1) idx = 0;
    return weatherCodeLabel(h.weather_code?.[idx]);
  }, [weather.data, now]);

  const currentTimeLabel = formatTimeLocal(now, tz);

  // Golden-hour countdown — null if we're inside one already, or if no
  // window in the next 3 days (shouldn't happen in Juneau May).
  const goldenCountdown = useMemo(() => {
    if (isGoldenHour(now, lat, lng)) return 'NOW';
    const win = nextGoldenHour(now, lat, lng);
    if (!win) return null;
    const ms = win.start.getTime() - now.getTime();
    const counted = formatGoldenCountdown(ms);
    return counted ? `GOLDEN HOUR IN ${counted}` : null;
  }, [now, lat, lng]);

  const sunsetMs = useMemo(() => {
    const d = new Date(now);
    d.setHours(21, 30, 0, 0);
    return d.getTime();
  }, [now]);

  // Sort + filter state. Lens granularity (28-70mm at 28mm vs at 35mm)
  // was over-categorisation for filtering. Three dimensions only:
  // player, camera body, activity_type.
  const [filterPlayer, setFilterPlayer] = useState('mine'); // 'mine' | 'all'
  const [filterCameraBody, setFilterCameraBody] = useState('all');
  const [filterActivity, setFilterActivity] = useState('all');
  const [sortBy, setSortBy] = useState('default');

  const scenarios = useMemo(() => data.data?.scenarios || [], [data.data]);
  const excursions = useMemo(
    () => excursionsData.data?.excursions || [],
    [excursionsData.data]
  );

  const scoringCtx = useMemo(
    () => ({
      activePlayerId,
      now,
      coords,
      excursions,
      sunsetMs,
    }),
    [activePlayerId, now, coords, excursions, sunsetMs]
  );

  const cameraBodies = useMemo(() => {
    const set = new Set();
    for (const s of scenarios) if (s.camera_body) set.add(s.camera_body);
    return Array.from(set).sort();
  }, [scenarios]);
  const activityTypes = useMemo(() => {
    const set = new Set();
    for (const s of scenarios) if (s.activity_type) set.add(s.activity_type);
    return Array.from(set).sort();
  }, [scenarios]);

  const filtered = useMemo(() => {
    let list = scenarios;
    if (filterPlayer === 'mine' && activePlayerId) {
      list = list.filter((s) => s.player_id === activePlayerId);
    }
    if (filterCameraBody !== 'all') {
      list = list.filter((s) => s.camera_body === filterCameraBody);
    }
    if (filterActivity !== 'all') {
      list = list.filter((s) => s.activity_type === filterActivity);
    }
    return list;
  }, [scenarios, filterPlayer, filterCameraBody, filterActivity, activePlayerId]);

  const sorted = useMemo(() => {
    if (sortBy === 'alpha') {
      return [...filtered]
        .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
        .map((scenario) => ({ scenario }));
    }
    if (sortBy === 'trigger') {
      return [...filtered]
        .sort((a, b) => (a.trigger || '').localeCompare(b.trigger || ''))
        .map((scenario) => ({ scenario }));
    }
    // 'default' — relevance to current conditions
    return rankScenarios(filtered, scoringCtx);
  }, [filtered, sortBy, scoringCtx]);

  return (
    <div className="mx-auto w-full max-w-screen-sm space-y-4 p-4">
      <header>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
          field reference · settings + audio
        </p>
        <h1 className="mt-1 font-serif text-2xl">Photo Coaching</h1>
      </header>

      {/* Right-now condition strip — single horizontal row */}
      <ConditionStrip
        weatherLabel={currentWeatherLabel}
        timeLabel={currentTimeLabel}
        goldenCountdown={goldenCountdown}
        coordsStatus={coordsStatus}
        weatherLoading={weather.loading}
      />

      {data.loading && (
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          …loading scenarios
        </p>
      )}
      {data.error && (
        <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          coaching unavailable — {data.error.message}
        </div>
      )}

      {/* Filter chip groups */}
      <div className="space-y-2">
        <ChipRow label="player">
          <FilterChip id="mine" current={filterPlayer} onClick={setFilterPlayer} label="Mine" />
          <FilterChip id="all" current={filterPlayer} onClick={setFilterPlayer} label="All players" />
        </ChipRow>

        {cameraBodies.length > 1 && (
          <ChipRow label="camera body">
            <FilterChip id="all" current={filterCameraBody} onClick={setFilterCameraBody} label="Any body" />
            {cameraBodies.map((b) => (
              <FilterChip
                key={b}
                id={b}
                current={filterCameraBody}
                onClick={setFilterCameraBody}
                label={b.replace(/^(Canon EOS |Sony )/, '')}
              />
            ))}
          </ChipRow>
        )}

        {activityTypes.length > 1 && (
          <ChipRow label="activity">
            <FilterChip id="all" current={filterActivity} onClick={setFilterActivity} label="Any" />
            {activityTypes.map((a) => (
              <FilterChip
                key={a}
                id={a}
                current={filterActivity}
                onClick={setFilterActivity}
                label={ACTIVITY_TYPE_LABELS[a] || a}
              />
            ))}
          </ChipRow>
        )}

        <div className="flex items-center gap-2">
          <label className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            sort
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="rounded-md border border-border-tertiary bg-bg-primary px-2 py-1 font-mono text-[11px]"
          >
            <option value="default">Default · relevance to now</option>
            <option value="alpha">Alphabetical</option>
            <option value="trigger">By trigger time</option>
          </select>
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            {sorted.length} of {scenarios.length}
          </span>
        </div>
      </div>

      <ul className="space-y-3">
        {sorted.map((entry) => (
          <ScenarioCard key={entry.scenario.id} scenario={entry.scenario} />
        ))}
        {sorted.length === 0 && !data.loading && (
          <li className="card px-4 py-6 text-center font-serif italic text-fg-secondary">
            No scenarios match the current filters.
          </li>
        )}
      </ul>

      <p className="px-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        aashna skips coaching · iphone-only · per camera profile rule
      </p>
    </div>
  );
}

function ConditionStrip({ weatherLabel, timeLabel, goldenCountdown, coordsStatus, weatherLoading }) {
  const parts = [];
  if (weatherLabel) parts.push(weatherLabel);
  else if (weatherLoading) parts.push('…WEATHER');
  if (timeLabel) parts.push(timeLabel);
  if (goldenCountdown) parts.push(goldenCountdown);
  return (
    <div className="card flex items-center justify-between gap-2 border-action/40 bg-action/5 px-3 py-2">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-action-fg/80">
        right now
      </p>
      <p className="text-right font-mono text-[11px] uppercase tracking-[0.14em] text-fg-primary">
        {parts.length > 0 ? parts.join(' · ') : '…tuning in'}
      </p>
      {coordsStatus === 'cached' && (
        <span className="hidden font-mono text-[9px] uppercase tracking-wider text-fg-tertiary sm:inline">
          last-known
        </span>
      )}
    </div>
  );
}

function ChipRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="mr-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {label}
      </span>
      {children}
    </div>
  );
}

function FilterChip({ id, current, onClick, label }) {
  const selected = current === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={[
        'rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider transition',
        selected
          ? 'border-action bg-action text-action-fg'
          : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// Settings table is the dominant element. Body is bumped one tier
// (text-base for fields, text-base for values) so the row is scannable
// at arm's length on a moving boat. Audio + composition note land
// below; "why this works" is collapsed by default.
function ScenarioCard({ scenario, priority = false }) {
  const [whyOpen, setWhyOpen] = useState(false);
  const s = scenario.settings || {};
  const rows = [
    { label: 'mode', value: s.mode },
    { label: 'shutter', value: s.shutter },
    { label: 'aperture', value: s.aperture },
    { label: 'iso', value: s.iso },
    { label: 'af', value: s.af },
    { label: 'drive', value: s.drive },
    { label: 'filter', value: s.filter },
    { label: 'support', value: s.support },
  ].filter((r) => r.value);

  return (
    <li
      className={[
        'card overflow-hidden',
        priority ? 'border-action/50' : '',
      ].join(' ')}
    >
      <header className="space-y-1 border-b border-border-tertiary bg-bg-secondary px-4 py-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {scenario.activity}
          {scenario.light_conditions ? ` · ${scenario.light_conditions}` : ''}
        </p>
        <h3 className="font-serif text-lg leading-tight">{scenario.label}</h3>
        <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {scenario.camera_body ? `${scenario.camera_body} · ` : ''}
          {scenario.lens}
        </p>
      </header>

      <table className="w-full table-fixed border-collapse text-base">
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.label}
              className={[
                'align-top',
                i % 2 === 0 ? 'bg-bg-primary' : 'bg-bg-secondary/40',
              ].join(' ')}
            >
              <th
                scope="row"
                className="w-20 px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider text-fg-tertiary"
              >
                {r.label}
              </th>
              <td className="px-3 py-2 text-base text-fg-primary">{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {scenario.composition_note && (
        <p className="border-t border-border-tertiary px-4 py-3 text-[15px] leading-relaxed text-fg-secondary">
          {scenario.composition_note}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2 border-t border-border-tertiary bg-bg-secondary px-4 py-2">
        {scenario.audio_url ? (
          <audio
            controls
            preload="none"
            src={scenario.audio_url}
            className="h-8 w-full"
            aria-label={`Mr. Wilman on ${scenario.label}`}
          />
        ) : (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            audio · pending render
          </p>
        )}
      </div>

      {scenario.why_this_works && (
        <div className="border-t border-border-tertiary">
          <button
            type="button"
            onClick={() => setWhyOpen((v) => !v)}
            className="flex w-full items-center justify-between px-4 py-2 font-mono text-[10px] uppercase tracking-wider text-action transition hover:bg-bg-tertiary"
            aria-expanded={whyOpen}
          >
            <span>why this works</span>
            <span aria-hidden className="text-fg-tertiary">{whyOpen ? '−' : '+'}</span>
          </button>
          {whyOpen && (
            <p className="border-t border-border-tertiary px-4 py-3 text-[15px] leading-relaxed text-fg-secondary">
              {scenario.why_this_works}
            </p>
          )}
        </div>
      )}
    </li>
  );
}
